import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { Sparkles, FlaskConical, Heart } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import api from "../lib/api";

const ACCORDIONS = [
  { key: "fun_facts", label: "Fun Facts", Icon: Sparkles },
  { key: "top_products", label: "Top 2 Dermagenix Products", Icon: FlaskConical },
  { key: "favorite_treatment", label: "Can't Live Without Treatment", Icon: Heart },
];

export default function Team() {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="team-page">
      <Seo title="Our Team" description="Meet the expert providers and injectors at Iridescent Medical & Wellness Spa in Clinton Township, MI — dedicated to artistry, safety and beautiful results." path="/team" />
      <PageHeader
        overline="Our Team"
        title="The faces behind your glow"
        subtitle="Meet the expert providers of Iridescent Medical & Wellness Spa — a team devoted to artistry, science, and making you feel your absolute best."
      />

      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-20 md:pb-28 space-y-12 md:space-y-16">
        {team.map((m, i) => (
          <TeamMember key={m.id || i} member={m} flip={i % 2 === 1} />
        ))}
        {team.length === 0 && (
          <p className="text-center text-[#8A9492]" data-testid="team-empty">
            Our team will be introduced here shortly.
          </p>
        )}
      </section>
    </div>
  );
}

function TeamMember({ member, flip }) {
  const accordions = ACCORDIONS.filter(({ key }) => (member[key] || "").trim());

  return (
    <Reveal
      className={`grid md:grid-cols-[260px_1fr] gap-6 md:gap-10 items-center ${
        flip ? "md:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`team-member-${member.id}`}
    >
      <div className="relative mx-auto w-48 md:w-full max-w-[260px]">
        <div className="absolute -inset-2 iri-gradient rounded-[1.5rem] opacity-40 blur-lg" aria-hidden />
        {member.photo_url ? (
          <img
            src={member.photo_url}
            alt={member.name}
            className="relative w-full aspect-[4/5] object-cover rounded-[1.25rem]"
            data-testid={`team-photo-${member.id}`}
          />
        ) : (
          <div
            className="relative w-full aspect-[4/5] rounded-[1.25rem] iri-gradient flex items-center justify-center"
            data-testid={`team-photo-${member.id}`}
          >
            <span className="font-heading text-4xl font-light text-ink/70">
              {(member.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}
            </span>
          </div>
        )}
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-iri-mid">{member.title}</p>
        <h2 className="mt-1.5 font-heading text-2xl md:text-3xl font-light tracking-tight text-ink">
          {member.name}
        </h2>
        {member.bio && (
          <p className="mt-3 text-sm md:text-base text-[#5C6664] font-light leading-relaxed">
            {member.bio}
          </p>
        )}

        {accordions.length > 0 && (
          <Accordion type="single" collapsible className="mt-4" data-testid={`team-accordion-${member.id}`}>
            {accordions.map(({ key, label, Icon }) => (
              <AccordionItem key={key} value={key} className="border-slate-200">
                <AccordionTrigger className="text-ink hover:no-underline text-base" data-testid={`team-acc-${key}-${member.id}`}>
                  <span className="flex items-center gap-2.5">
                    <Icon className="h-4 w-4 text-iri-mid" strokeWidth={1.75} /> {label}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[#5C6664] whitespace-pre-line leading-relaxed">
                  {member[key]}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </Reveal>
  );
}
